import React, { useState } from 'react';
import './Pagination.css';

const Pagination = ({ 
  currentPage, 
  totalPages, 
  onPageChange, 
  totalItems, 
  itemsPerPage, 
  onItemsPerPageChange,
  loading,
  isAdmin,
  levelFilter,
  tempLevelFilter,
  onLevelFilterChange,
  onApplyLevelFilter,
  onClearLevelFilter,
  levelFilterError,
  rangeStart,
  rangeEnd,
}) => {
  const [goToPage, setGoToPage] = useState('');

  const handleLevelFilterKeyPress = (e) => {
    if (e.key === 'Enter' && (tempLevelFilter?.min || tempLevelFilter?.max)) {
      onApplyLevelFilter();
    }
  };

  const getVisiblePages = () => {
    if (!totalPages || totalPages < 1) {
      return [];
    }
    if (totalPages === 1) {
      return [1];
    }

    const delta = 2;
    const pages = new Set([1, totalPages, currentPage]);

    for (let i = currentPage - delta; i <= currentPage + delta; i += 1) {
      if (i >= 1 && i <= totalPages) pages.add(i);
    }

    const sorted = Array.from(pages).sort((a, b) => a - b);
    const withDots = [];

    sorted.forEach((page, index) => {
      if (index > 0 && page - sorted[index - 1] > 1) {
        withDots.push('...');
      }
      withDots.push(page);
    });

    return withDots;
  };

  const handleGoToPage = (e) => {
    e.preventDefault();
    const pageNumber = parseInt(goToPage, 10);
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      onPageChange(pageNumber);
      setGoToPage('');
    }
  };

  const handleGoToPageInput = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setGoToPage(value);
    }
  };

  const canGoPrev = currentPage > 1 && totalPages > 0;
  const canGoNext = currentPage < totalPages;

  return (
    <div className="pagination-container">
      {isAdmin ? (
        <div className="level-filter">
          <label>Level Filter:</label>
          <input
            type="number"
            placeholder="Min"
            value={tempLevelFilter?.min || ''}
            onChange={(e) => onLevelFilterChange && onLevelFilterChange('min', e.target.value)}
            onKeyDown={handleLevelFilterKeyPress}
            min="0"
            max="100000"
            className="level-filter-input"
          />
          <span>-</span>
          <input
            type="number"
            placeholder="Max"
            value={tempLevelFilter?.max || ''}
            onChange={(e) => onLevelFilterChange && onLevelFilterChange('max', e.target.value)}
            onKeyDown={handleLevelFilterKeyPress}
            min="0"
            max="100000"
            className="level-filter-input"
          />
          <button
            className="apply-level-filter-btn"
            onClick={onApplyLevelFilter}
            disabled={!tempLevelFilter?.min && !tempLevelFilter?.max}
          >
            Apply
          </button>
          {(levelFilter?.min || levelFilter?.max) && (
            <button
              className="clear-level-filter-btn"
              onClick={onClearLevelFilter}
              title="Clear level filter"
            >
              Clear
            </button>
          )}
          {levelFilterError && (
            <span className="level-filter-error">{levelFilterError}</span>
          )}
        </div>
      ): (<div></div>)}
      <div className="pagination-info">
        <div className="pagination-controls">
          <div className="pagination">
            <button
              className="page-btn prev-btn"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={!canGoPrev}
              aria-label="Previous page"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {getVisiblePages().map((page, index) => (
              <React.Fragment key={`${page}-${index}`}>
                {page === '...' ? (
                  <span className="page-dots">...</span>
                ) : (
                  <button
                    type="button"
                    className={`page-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => currentPage !== page && onPageChange(page)}
                  >
                    {page}
                  </button>
                )}
              </React.Fragment>
            ))}

            <button
              className="page-btn next-btn"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={!canGoNext}
              aria-label="Next page"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          <form className="go-to-page" onSubmit={handleGoToPage}>
            <label htmlFor="page-input">Go to:</label>
            <input
              id="page-input"
              type="text"
              value={goToPage}
              onChange={handleGoToPageInput}
              placeholder="Page"
              className="page-input"
              min="1"
              max={totalPages}
            />
            <button 
              type="submit" 
              className="go-btn"
              disabled={!goToPage || parseInt(goToPage, 10) < 1 || parseInt(goToPage, 10) > totalPages}
            >
              Go
            </button>
          </form>
        </div>

        <div className="items-count">
          <div className="items-per-page">
            <label>Show: </label>
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange && onItemsPerPageChange(parseInt(e.target.value, 10))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span> per page</span>
          </div>

          <span className="showing-info">
            {loading && !totalItems
              ? 'Loading...'
              : totalItems === 0
                ? '0 Co-founders'
                : totalPages > 1
                  ? `Showing ${rangeStart ?? ((currentPage - 1) * itemsPerPage + 1)}–${rangeEnd ?? Math.min(currentPage * itemsPerPage, totalItems)} of ${totalItems.toLocaleString()} · Page ${currentPage} of ${totalPages}`
                  : `${totalItems.toLocaleString()} Co-founder${totalItems === 1 ? '' : 's'}`}
          </span>
        </div>
      </div>
    </div>
  );
};

export default Pagination;